/* The RegO shield "fills like water": faded logo behind, full-colour logo revealed by a rising animated water surface. */
import { useEffect } from "react";
import { useSimulatedProgress } from "../hooks/useSimulatedProgress";

const SHIELD_L = "M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3";
const SHIELD_R = "M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3";
const SHIELD_CHECK = "M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z";

const SHIELD_INNER_L = "M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z";
const SHIELD_INNER_R = "M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z";
// A wave surface at y=0, body down to y=44; spans x=-8..72 so a -16 (one wavelength) shift loops seamlessly.
const WAVE_PATH = "M-8 0 q4 -1.6 8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 t8 0 V44 H-8 Z";

/* `pct` sets the water level (0-100). */
export function RegoWaterShield({ pct }: { pct: number }) {
  const level = 32 * (1 - Math.min(100, Math.max(0, pct)) / 100);
  const logo = (opacity?: number) => (
    <>
      <path d={SHIELD_L} fill="url(#rl-1)" opacity={opacity} />
      <path d={SHIELD_R} fill="url(#rl-2)" opacity={opacity} />
      <path d={SHIELD_INNER_L} fill="url(#rl-1)" opacity={(opacity ?? 1) * 0.7} />
      <path d={SHIELD_INNER_R} fill="url(#rl-2)" opacity={(opacity ?? 1) * 0.7} />
    </>
  );
  return (
    <svg viewBox="0 0 32 32" className="w-full h-full" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="rl-1" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#A18DC2" /><stop offset="50%" stopColor="#BAC3E3" /><stop offset="100%" stopColor="#A18DC2" />
        </linearGradient>
        <linearGradient id="rl-2" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#CE7EB3" /><stop offset="50%" stopColor="#D6A0C8" /><stop offset="100%" stopColor="#A090C4" />
        </linearGradient>
        <clipPath id="rw-shield"><path d={SHIELD_L} /><path d={SHIELD_R} /></clipPath>
        <mask id="rw-water">
          {/* vertical water level (smoothly transitions as progress rises) */}
          <g style={{ transform: `translateY(${level}px)`, transition: "transform 0.6s ease-out" }}>
            {/* horizontal wobble = the moving water surface */}
            <path fill="#fff" d={WAVE_PATH}>
              <animateTransform attributeName="transform" type="translate" values="0 0;-16 0" dur="1.4s" repeatCount="indefinite" />
            </path>
          </g>
        </mask>
      </defs>
      {/* faded logo = empty state */}
      <g clipPath="url(#rw-shield)" opacity="0.16">{logo()}</g>
      {/* full-colour logo revealed by the rising wavy water */}
      <g clipPath="url(#rw-shield)" mask="url(#rw-water)">{logo()}</g>
      {/* check mark on top */}
      <path d={SHIELD_CHECK} fill="white" />
    </svg>
  );
}

/** Transparent water-fill shield + phase label. `complete` finishes the fill to 100% and fires
    `onDone`, when the caller should swap in real content; `pct` overrides the simulated climb
    when a real progress value is available. */
export function ReportWaterLoader({
  label = "Generating report…",
  complete = false,
  onDone,
  pct: controlledPct,
}: {
  label?: string;
  complete?: boolean;
  onDone?: () => void;
  pct?: number;
}) {
  const simulatedPct = useSimulatedProgress(92, 220, complete);
  const pct = controlledPct ?? simulatedPct;

  useEffect(() => {
    if (pct === 100 && onDone) {
      const t = setTimeout(onDone, 400);
      return () => clearTimeout(t);
    }
  }, [pct, onDone]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-5">
      <div className="relative w-32 h-32" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <RegoWaterShield pct={pct} />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
          {label} <span className="font-semibold tabular-nums text-indigo-600 dark:text-indigo-400">{pct}%</span>
        </p>
      </div>
    </div>
  );
}
